import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import MainLayout from '@/components/layout/MainLayout';
import FileRow from '@/components/qr/FileRow';
import MediaLightbox from '@/components/qr/MediaLightbox';
import PanoLightbox from '@/components/qr/PanoLightbox';
import { getProjectBySlug, ResolvedProject } from '@/projects';
import ExplorerBar from '@/components/explorer/ExplorerBar';
import PinGate from '@/components/auth/PinGate';
import { isUnlocked, setUnlocked, verifyQrTicket } from '@/lib/access';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type MediaKind = 'images' | 'videos' | 'panos';

const getAssetList = (project: ResolvedProject, kind: MediaKind) => {
  switch (kind) {
    case 'images':
      return project.assets.images;
    case 'videos':
      return project.assets.videos;
    case 'panos':
      return project.assets.panos;
    default:
      return [];
  }
};

const MediaExplorer: React.FC = () => {
  const { t } = useTranslation();
  const { slug, kind } = useParams<{ slug: string; kind: MediaKind }>();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const qr = searchParams.get('qr');
  const ticket = searchParams.get('t') || '';

  const project = slug ? getProjectBySlug(slug) : undefined;
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [authorized, setAuthorized] = useState(false);
  const [checking, setChecking] = useState(true);
  const [ticketError, setTicketError] = useState<string | null>(null);

  const derivedKind: MediaKind | undefined = useMemo(() => {
    if (kind === 'images' || kind === 'videos' || kind === 'panos') return kind;
    if (location.pathname.endsWith('/images')) return 'images';
    if (location.pathname.endsWith('/videos')) return 'videos';
    if (location.pathname.endsWith('/panos')) return 'panos';
    return undefined;
  }, [kind, location.pathname]);

  const assets = useMemo(() => {
    if (!project || !derivedKind) return [];
    return getAssetList(project, derivedKind);
  }, [project, derivedKind]);

  useEffect(() => {
    let mounted = true;
    const checkAccess = async () => {
      if (!slug || !project) {
        setChecking(false);
        return;
      }

      const unlocked = await isUnlocked(slug);
      if (!mounted) return;
      if (unlocked) {
        setAuthorized(true);
        setChecking(false);
        return;
      }

      if (qr === '1' && ticket) {
        if (verifyQrTicket(project, ticket)) {
          await setUnlocked(slug, 'ticket');
          if (!mounted) return;
          setAuthorized(true);
          setChecking(false);
          navigate(location.pathname, { replace: true });
          return;
        }
        setTicketError(t('pinGate.ticketError'));
      }

      setChecking(false);
    };

    checkAccess();
    return () => {
      mounted = false;
    };
  }, [slug, project, qr, ticket, location.pathname, navigate, t]);

  const closeOverlay = () => setActiveIndex(null);

  if (!project || !derivedKind) {
    return (
      <MainLayout>
        <div className="pt-32 pb-24 bg-[#2A2A2A]">
          <div className="container-custom text-center text-[#E0E0E0]">
            <p className="text-xl">{t('common.projectNotFound')}</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (checking) {
    return (
      <MainLayout>
        <div className="pt-32 pb-24 bg-[#2A2A2A] min-h-screen">
          <div className="container-custom text-center text-[#E0E0E0]">{t('common.checkingAccess')}</div>
        </div>
      </MainLayout>
    );
  }

  if (!authorized) {
    return (
      <MainLayout>
        <PinGate
          slug={project.slug}
          projectTitle={project.info.title}
          message={ticketError || undefined}
          onSuccess={() => setAuthorized(true)}
        />
      </MainLayout>
    );
  }

  const kindLabel = derivedKind === 'panos' ? t('qr.panos') : t(`qr.${derivedKind}`);

  return (
    <MainLayout>
      <div className="pt-28 sm:pt-32 pb-20 bg-[#2A2A2A] min-h-screen">
        <div className="container-custom space-y-6 animate-[fadeIn_0.2s_ease]">
          <ExplorerBar
            breadcrumbs={[
              { label: t('common.explorer'), to: `/p/${project.slug}` },
              { label: project.info.title, to: `/p/${project.slug}` },
              { label: kindLabel, to: `/p/${project.slug}/${derivedKind}` }
            ]}
          />
          <div className="flex items-center gap-3">
            <Link
              to={`/p/${project.slug}`}
              className="text-[#E0E0E0] hover:text-[#C8102E] flex items-center gap-2"
            >
              <ArrowLeft size={18} />
              {t('common.back')}
            </Link>
            <span className="text-[#888888] text-sm capitalize">{kindLabel}</span>
          </div>

          <div className="space-y-3">
            {assets.length === 0 && (
              <div className="text-[#E0E0E0] bg-[#232323] rounded-xl p-6">{t('common.noFiles')}</div>
            )}
            {assets.map((asset, index) => (
              <FileRow
                key={`${asset.file}-${asset.ext}`}
                name={asset.name || `${asset.file}.${asset.ext}`}
                kind={derivedKind}
                thumbnail={asset.url}
                onClick={() => setActiveIndex(index)}
              />
            ))}
          </div>
        </div>
      </div>

      {activeIndex !== null && assets[activeIndex] && (
        derivedKind === 'panos' ? (
          <PanoLightbox src={assets[activeIndex].url} onClose={closeOverlay} />
        ) : (
          <MediaLightbox
            src={assets[activeIndex].url}
            kind={derivedKind === 'videos' ? 'video' : 'image'}
            onClose={closeOverlay}
          />
        )
      )}
    </MainLayout>
  );
};

export default MediaExplorer;
